import React, { memo, useEffect, useRef, useState } from "react";
import { Animated, Pressable, Text, View } from "react-native";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useIsFocused } from "@react-navigation/native";
import { useCssStyle } from "../../../customHook/getCssStyle";
import { getCleanUrl } from "../../../utils/Helper";
import { myStayTab, notificationKey, tabphone } from "../../../config/Constant";
import { setRedirectToTabId } from "../../../Redux/Reducers/appConfigReducer";
import { ImageComp } from "../../../components/Image/Image";


const TabBar_Landscape = memo(({ state, navigation, handleFooterNavClick }) => {

    const getStyles = useCssStyle();
    const dispatch = useDispatch();
    const isFocused = useIsFocused();

    // Combine selectors to reduce re-renders
    const {
        isLoggedIn,
        redirectToTabId,
        isBookingFound,
        currentLayout,
        notifications,
        newMessagesCount,
        mwiStatus
    } = useSelector(state => ({
        isLoggedIn: state.appConfigSlice.isLoggedIn,
        redirectToTabId: state.appConfigSlice.redirectToTabId,
        isBookingFound: state.checkInSlice.bookingStatus.isBookingFound,
        currentLayout: state.deviceConfigSlice.currentLayout,
        notifications: state.notificationSlice.notifications,
        newMessagesCount: state.notificationSlice.newMessagesCount,
        mwiStatus: state.softphoneSlice.mwiStatus,
    }), shallowEqual);

    const tabDetails = currentLayout?.tabDetails || [];

    const [tabBarHeight, setTabBarHeight] = useState(0);
    const translateY = useRef(new Animated.Value(0)).current;


    const getDefaultFooter = () => {
        let requiredTabIndex = 0;

        if (tabDetails?.length > 0) {
            if (redirectToTabId) {
                // Find the tab with the uniqueKey that matches initialTabId
                const requiredTabItem = tabDetails.findIndex(tabItem =>
                    redirectToTabId.includes(tabItem.uniqueKey.replace(/ /g, '').toLowerCase())
                );
                if (requiredTabItem >= 0) {
                    requiredTabIndex = requiredTabItem;
                }
            } else if (isLoggedIn || isBookingFound) {
                // Find the "my stay" tab
                const myStayTabItem = tabDetails.findIndex(tabItem =>
                    myStayTab.includes(tabItem.uniqueKey.replace(/ /g, '').toLowerCase())
                );

                if (myStayTabItem >= 0) {
                    requiredTabIndex = myStayTabItem;
                }
            }


            if (state.index != requiredTabIndex) {
                setTimeout(() => {
                    handleFooterNavClick(tabDetails[requiredTabIndex], navigation)
                }, 0);
            }

        }
    };

    useEffect(() => {

        if (redirectToTabId) {
            getDefaultFooter();
            dispatch(setRedirectToTabId(null));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [redirectToTabId])

    useEffect(() => {
        // select required footer tab
        if (isLoggedIn || (isBookingFound && isFocused)) {
            getDefaultFooter();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, isBookingFound]);




    useEffect(() => {
        if (tabBarHeight > 0) {
            // Accomodate for tab bar padding as we do absolute positioning.
            Animated.spring(translateY, {
                toValue: (state.index * tabBarHeight),
                useNativeDriver: false,
            }).start();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.index, tabBarHeight]);



    return (
        <View style={getStyles(["tabBarStyle_landscape"])}
            onLayout={(event) => {
                // Accomodate for a tab bar narrower than the screen as we do absolute positioning.
                const styles = getStyles(["tabContainerTopBorder_container_landscape"]) || {};
                const height = Number(styles.height) || 0;

                setTabBarHeight(height);
            }}
            id="tabBarStyle_landscape"
        >       
            <View style={getStyles(["tabParentContainer_landscape"])} id="tabParentContainer_landscape">

                {/* Sliding Indicator */}
                <Animated.View
                    style={[
                        getStyles(["tabContainerTopBorder_container_landscape",]),
                        {
                            transform: [{ translateY: translateY }],
                        },
                    ]}
                    id={"tabContainerTopBorder_container_landscape"}
                >
                    <View style={getStyles(["tabContainerTopBorder_landscape"])} />
                </Animated.View>


                {state.routes.map((route, index) => {
                    const tab = tabDetails.find((tab) => tab.uniqueKey === route.name);
                    const isFocused = state.index === index;

                    let newPing = 0;
                    let oldPing = 0;


                    if(notificationKey.includes(tab.uniqueKey)){
                        newPing = newMessagesCount || 0;
                        oldPing = notifications?.length || 0;
                    }

                    if (tabphone.includes(tab.uniqueKey)) {
                        newPing = mwiStatus?.nbNewMessages || 0;
                        oldPing = mwiStatus?.nbOldMessages || 0;                          
                    }

                    return (
                        <Pressable
                            key={route.key}
                            onPress={() => handleFooterNavClick(tab, navigation)}
                            style={({ pressed }) =>
                                getStyles([
                                    "tabContainer_landscape",
                                    `tabContainer_landscape_${tab.uniqueKey}`,
                                    isFocused && "tabFocusedContainer_landscape",
                                    {
                                        opacity: pressed ? 0.7 : 1,
                                        transform: [{ scale: pressed ? 0.95 : 1 }],
                                    },

                                ])
                            }
                            id={`tabContainer_landscape tabContainer_landscape_${tab.uniqueKey}`}
                        >
                            <View style={getStyles(["tabIconContainer_landscape"])} id={`tabIconContainer_landscape_${tab.uniqueKey}`}>
                                <ImageComp
                                    source={isFocused && tab.tabFocusedIconUrl ? getCleanUrl(tab.tabFocusedIconUrl) : getCleanUrl(tab?.tabIconUrl)}
                                    style={getStyles(["tabIcon_landscape", `tabIcon_landscape_${tab.uniqueKey}`,isFocused && "tabIconFocused_landscape", ])}
                                    id={`tabIcon_landscape tabIcon_landscape_${tab.uniqueKey}`}
                                />
                                {newPing > 0 && (
                                    <View style={getStyles(['notificationicon_countcontainer_unread'])}>
                                        <Text style={getStyles(['notificationicon_countcontainer_unread_text'])}>{newPing}</Text>
                                    </View>
                                )}
                                {newPing < 1 && oldPing > 0 && (
                                    <View style={getStyles(['notificationicon_countcontainer'])}>
                                        <Text style={getStyles(['notificationicon_countcontainer_text'])}>{oldPing}</Text>
                                    </View>
                                )}
                            </View>
                            <Text style={getStyles(["tabLabel_landscape", `tabLabel_landscape_${tab.uniqueKey}`, isFocused && "tabLabelFocused_landscape"])} id={`tabLabel_landscape_${tab.uniqueKey}`}>
                                {tab?.tabName}
                            </Text>
                        </Pressable>
                    );
                })}
            </View>
        </View>
    );
});

export default TabBar_Landscape